import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import API_BASE_URL from '../config';
import InterviewFeedbackForm from './InterviewFeedbackForm';
import ThankYouMessage from './ThankYouMessage';
import AIInterview from './AIInterview';

/* ─── Design Tokens ─── */
const T = {
  primary: '#1565C0',
  primaryDark: '#0D47A1',
  primaryLight: '#1976D2',
  accent: '#FF8F00',
  accentLight: '#FFA000',
  white: '#FFFFFF',
  bg: '#F5F7FA',
  surface: '#FFFFFF',
  surfaceHover: '#F0F4FF',
  text: '#1A2340',
  textSecondary: '#546E7A',
  textMuted: '#90A4AE',
  border: '#E3E8EF',
  borderFocus: '#1565C0',
  success: '#2E7D32',
  successBg: '#E8F5E9',
  successBdr: '#A5D6A7',
  errorBg: '#FFEBEE',
  errorBdr: '#EF9A9A',
  shadow: '0 2px 8px rgba(21,101,192,0.08)',
  shadowMd: '0 4px 20px rgba(21,101,192,0.12)',
  shadowLg: '0 8px 40px rgba(21,101,192,0.16)',
};

/* ─── Global Styles ─── */
const globalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Inter:wght@400;500;600&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Inter', sans-serif; background: ${T.bg}; color: ${T.text}; }
  input, textarea, button { font-family: 'Inter', sans-serif; }
  input:focus, textarea:focus { outline: none; border-color: ${T.borderFocus} !important; box-shadow: 0 0 0 3px rgba(21,101,192,0.1) !important; }
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.5; } }
  @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes slideIn { from { opacity: 0; transform: translateX(-12px); } to { opacity: 1; transform: translateX(0); } }
  @keyframes modalIn { from { opacity: 0; transform: scale(0.95) translateY(12px); } to { opacity: 1; transform: scale(1) translateY(0); } }
  .step-animate { animation: fadeIn 0.3s ease forwards; }
  .sidebar-item:hover { background: ${T.surfaceHover} !important; }
  .btn-primary:hover { background: ${T.primaryDark} !important; transform: translateY(-1px); box-shadow: ${T.shadowMd} !important; }
  .btn-outline:hover { background: #EEF4FF !important; }
  .btn-success:hover { background: #1B5E20 !important; transform: translateY(-1px); }
  .option-card:hover { border-color: ${T.primary} !important; background: ${T.surfaceHover} !important; }
  input[type=checkbox] { accent-color: ${T.primary}; }
  .modal-overlay { animation: fadeIn 0.2s ease forwards; }
  .modal-box { animation: modalIn 0.25s ease forwards; }
  ::-webkit-scrollbar { width: 6px; } ::-webkit-scrollbar-track { background: transparent; } ::-webkit-scrollbar-thumb { background: #CBD5E0; border-radius: 6px; }
`;

const S = {
  wrap: { background: T.bg, minHeight: '100vh', fontFamily: "'Inter', sans-serif" },
  navbar: {
    background: T.white, padding: '0 32px', height: 64,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    borderBottom: `1px solid ${T.border}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    position: 'sticky', top: 0, zIndex: 100,
  },
  logoWrap: { display: 'flex', alignItems: 'center', gap: 10 },
  logoIcon: {
    width: 36, height: 36, borderRadius: 8,
    background: `linear-gradient(135deg, ${T.primary}, ${T.primaryLight})`,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  logoText: { fontSize: 17, fontWeight: 700, color: T.primary, fontFamily: "'Poppins', sans-serif" },
  navRight: { display: 'flex', alignItems: 'center', gap: 12 },
  userChip: {
    display: 'flex', alignItems: 'center', gap: 8,
    background: T.bg, border: `1px solid ${T.border}`,
    borderRadius: 24, padding: '6px 14px 6px 6px',
  },
  userAvatar: {
    width: 28, height: 28, borderRadius: '50%',
    background: `linear-gradient(135deg, ${T.primary}, ${T.accent})`,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 11, fontWeight: 700, color: T.white,
  },
  userName: { fontSize: 13, fontWeight: 500, color: T.text },
  body: { display: 'grid', gridTemplateColumns: '260px 1fr', minHeight: 'calc(100vh - 64px)' },
  sidebar: {
    background: T.white, borderRight: `1px solid ${T.border}`,
    padding: '28px 0', display: 'flex', flexDirection: 'column',
    position: 'sticky', top: 64, height: 'calc(100vh - 64px)', overflowY: 'auto',
  },
  sidebarHead: { padding: '0 24px 20px', borderBottom: `1px solid ${T.border}` },
  sidebarTitle: { fontSize: 11, fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '1.2px', marginBottom: 12 },
  progressWrap: { marginBottom: 6 },
  progressTrack: { height: 6, background: T.border, borderRadius: 6, overflow: 'hidden' },
  progressFill: (pct) => ({
    height: '100%', width: `${pct}%`,
    background: `linear-gradient(90deg, ${T.primary}, ${T.accent})`,
    borderRadius: 6, transition: 'width 0.5s ease',
  }),
  progressLabel: { fontSize: 12, color: T.textSecondary, marginTop: 6, display: 'flex', justifyContent: 'space-between' },
  sidebarSteps: { flex: 1, padding: '16px 0', overflowY: 'auto' },
  stepItem: (active, done) => ({
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '11px 24px', cursor: 'pointer',
    background: active ? '#EEF4FF' : 'transparent',
    borderLeft: active ? `3px solid ${T.primary}` : '3px solid transparent',
    transition: 'all 0.2s',
  }),
  stepBubble: (active, done) => ({
    width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 11, fontWeight: 700,
    background: done ? T.primary : active ? T.white : T.bg,
    border: done ? `2px solid ${T.primary}` : active ? `2px solid ${T.primary}` : `2px solid ${T.border}`,
    color: done ? T.white : active ? T.primary : T.textMuted,
    boxShadow: active ? `0 0 0 4px rgba(21,101,192,0.12)` : 'none',
  }),
  stepLabel: (active, done) => ({
    fontSize: 13, fontWeight: active ? 600 : done ? 500 : 400,
    color: active ? T.primary : done ? T.text : T.textMuted,
    lineHeight: 1.3,
  }),
  main: { padding: '40px', display: 'flex', flexDirection: 'column', maxWidth: 720 },
  stepTag: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    background: '#EEF4FF', color: T.primary,
    fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 20,
    marginBottom: 14, width: 'fit-content', letterSpacing: '0.3px',
  },
  stepTagDot: { width: 6, height: 6, borderRadius: '50%', background: T.primary },
  h2: { fontSize: 22, fontWeight: 700, color: T.text, marginBottom: 6, fontFamily: "'Poppins', sans-serif" },
  subtitle: { fontSize: 14, color: T.textSecondary, marginBottom: 28, lineHeight: 1.6 },
  fieldLabel: {
    display: 'block', fontSize: 12, fontWeight: 600,
    color: T.textSecondary, textTransform: 'uppercase',
    letterSpacing: '0.8px', marginBottom: 6,
  },
  input: {
    width: '100%', padding: '11px 14px', fontSize: 14,
    background: T.white, border: `1.5px solid ${T.border}`,
    borderRadius: 8, color: T.text, marginBottom: 16, transition: 'all 0.2s',
  },
  inputReadonly: {
    width: '100%', padding: '11px 14px', fontSize: 14,
    background: T.bg, border: `1.5px solid ${T.border}`,
    borderRadius: 8, color: T.textSecondary, marginBottom: 16, cursor: 'not-allowed',
  },
  card: {
    background: T.white, border: `1px solid ${T.border}`,
    borderRadius: 12, padding: '20px 24px', marginBottom: 20, boxShadow: T.shadow,
  },
  detailRow: (last) => ({
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 0',
    borderBottom: last ? 'none' : `1px solid ${T.border}`,
  }),
  detailKey: { fontSize: 13, color: T.textSecondary, fontWeight: 500 },
  detailVal: { fontSize: 13, fontWeight: 600, color: T.text },
  infoBox: {
    background: '#EEF4FF', border: `1px solid #C5D8FF`,
    borderRadius: 10, padding: '14px 18px', marginBottom: 20,
    display: 'flex', gap: 12, alignItems: 'flex-start',
  },
  infoIcon: { fontSize: 16, flexShrink: 0, marginTop: 1 },
  infoText: { fontSize: 13, color: '#1E3A8A', lineHeight: 1.6 },
  warningBox: {
    background: '#FFF8E1', border: `1px solid #FFE082`,
    borderRadius: 10, padding: '14px 18px', marginBottom: 20,
    display: 'flex', gap: 12, alignItems: 'flex-start',
  },
  warningText: { fontSize: 13, color: '#5F4100', lineHeight: 1.6 },
  disclaimerCard: {
    background: T.white, border: `1px solid ${T.border}`,
    borderRadius: 12, padding: '20px 24px', marginBottom: 24, boxShadow: T.shadow,
  },
  disclaimerTitle: {
    fontSize: 14, fontWeight: 700, color: T.text,
    marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8,
  },
  disclaimerItem: {
    display: 'flex', gap: 10, alignItems: 'flex-start',
    padding: '8px 0', borderBottom: `1px solid ${T.border}`,
    fontSize: 13, color: T.textSecondary, lineHeight: 1.6,
  },
  sysGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 },
  sysItem: (passed) => ({
    display: 'flex', alignItems: 'center', gap: 14,
    padding: '16px 18px',
    background: passed ? T.successBg : T.white,
    border: `1.5px solid ${passed ? T.successBdr : T.border}`,
    borderRadius: 10, transition: 'all 0.3s',
  }),
  sysIconWrap: (passed) => ({
    width: 40, height: 40, borderRadius: 10,
    background: passed ? '#C8E6C9' : T.bg,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  }),
  sysName: { fontSize: 13, fontWeight: 600, color: T.text },
  sysStatus: (passed) => ({ fontSize: 12, color: passed ? T.success : T.textMuted, marginTop: 2 }),
  sysStatusDot: (passed) => ({
    width: 8, height: 8, borderRadius: '50%',
    background: passed ? T.success : T.border, marginLeft: 'auto',
  }),
  videoBox: {
    background: '#111', borderRadius: 12, overflow: 'hidden', marginBottom: 20,
    border: `1px solid ${T.border}`, aspectRatio: '16/9', maxHeight: 200,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  photoWrap: {
    position: 'relative', background: '#111',
    borderRadius: 12, overflow: 'hidden', marginBottom: 20,
    border: `1px solid ${T.border}`, height: 240,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  faceGuide: {
    position: 'absolute', width: 150, height: 150,
    border: '2px dashed rgba(255,255,255,0.6)',
    borderRadius: '50%', pointerEvents: 'none',
  },
  phraseCard: {
    background: T.bg, borderLeft: `4px solid ${T.primary}`,
    borderRadius: '0 10px 10px 0', padding: '16px 20px', marginBottom: 24,
  },
  phraseLabel: { fontSize: 11, fontWeight: 700, color: T.primary, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 8 },
  phraseText: { fontSize: 15, color: T.text, fontStyle: 'italic', lineHeight: 1.6 },
  otpRow: { display: 'flex', gap: 8, marginBottom: 8 },
  otpInput: {
    width: 44, height: 52, textAlign: 'center', fontSize: 20, fontWeight: 700,
    background: T.white, border: `1.5px solid ${T.border}`,
    borderRadius: 8, color: T.text, transition: 'all 0.2s',
  },
  timerText: { fontSize: 12, color: T.primary, marginBottom: 12, fontWeight: 500 },
  captchaRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 },
  captchaBox: {
    padding: '10px 16px', background: T.bg, border: `1.5px solid ${T.border}`,
    borderRadius: 8, fontSize: 20, fontWeight: 800, letterSpacing: 8,
    color: T.primary, fontFamily: 'monospace', userSelect: 'none',
  },
  captchaRefresh: {
    background: T.white, border: `1.5px solid ${T.border}`, borderRadius: 8,
    padding: '10px 12px', cursor: 'pointer', color: T.textSecondary, fontSize: 16, transition: 'all 0.2s',
  },
  captchaInput: {
    flex: 1, padding: '10px 14px', background: T.white,
    border: `1.5px solid ${T.border}`, borderRadius: 8, color: T.text, fontSize: 14,
  },
  ctaCard: {
    background: T.white, border: `1px solid ${T.border}`,
    borderRadius: 16, padding: '40px', textAlign: 'center', boxShadow: T.shadow, marginTop: 8,
  },
  ctaIconWrap: {
    width: 72, height: 72, borderRadius: '50%',
    background: '#EEF4FF', border: `2px solid #C5D8FF`,
    display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px',
  },
  ctaTitle: { fontSize: 20, fontWeight: 700, color: T.text, marginBottom: 10, textAlign: 'center', alignItems: 'center', fontFamily: "'Poppins', sans-serif" },
  ctaText: { fontSize: 14, color: T.textSecondary, marginBottom: 28, lineHeight: 1.6 },
  btnRow: { display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 4 },
  btnPrimary: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '11px 24px', fontSize: 14, fontWeight: 600,
    background: T.primary, color: T.white, border: 'none', borderRadius: 8, cursor: 'pointer',
    transition: 'all 0.2s', boxShadow: `0 2px 8px rgba(21,101,192,0.2)`,
  },
  btnOutline: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '10px 20px', fontSize: 14, fontWeight: 600,
    background: T.white, color: T.primary,
    border: `1.5px solid ${T.primary}`, borderRadius: 8, cursor: 'pointer', transition: 'all 0.2s',
  },
  btnSuccess: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '11px 24px', fontSize: 14, fontWeight: 600,
    background: T.success, color: T.white, border: 'none', borderRadius: 8, cursor: 'pointer',
    transition: 'all 0.2s', boxShadow: `0 2px 8px rgba(46,125,50,0.2)`,
  },
  btnLarge: { padding: '14px 36px', fontSize: 15 },
  divider: { height: 1, background: T.border, margin: '20px 0' },
  loadingWrap: {
    background: T.bg, minHeight: '100vh',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexDirection: 'column', gap: 16,
  },
  kioskContainer: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: '#000', zIndex: 99999, display: 'flex', flexDirection: 'column' },
  kioskHeader: { background: '#0D1B4B', padding: '10px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.1)' },
  kioskEndCallBtn: { background: '#DC2626', color: 'white', border: 'none', padding: '7px 18px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 },
  kioskIframe: { flex: 1, width: '100%', border: 'none' },
  exitOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100001 },
  exitBox: { background: '#fff', padding: '32px', borderRadius: '16px', textAlign: 'center', maxWidth: '400px', width: '90%', boxShadow: '0 24px 60px rgba(0,0,0,0.3)' },
  lockdownWarning: {
    position: 'fixed', top: 0, left: 0, right: 0,
    background: '#DC2626', color: 'white',
    textAlign: 'center', padding: '12px',
    zIndex: 200000, fontWeight: 700, fontSize: '14px',
  },
};

/* ─── Step 8 specific styles ─── */
const styles = {
  cameraSetupCard: {
    background: T.white, borderRadius: '16px', border: `1px solid ${T.border}`,
    overflow: 'hidden', marginBottom: '24px', boxShadow: T.shadow,
  },
  cameraHeader: {
    padding: '16px 20px', background: '#F8FAFE', borderBottom: `1px solid ${T.border}`,
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  cameraTitle: { fontSize: '14px', fontWeight: '600', color: T.text },
  cameraStatus: { fontSize: '12px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '8px' },
  cameraPreview: {
    background: '#111', height: '240px',
    display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative',
  },
  previewVideo: { width: '100%', height: '100%', objectFit: 'cover' },
  cameraPlaceholder: { textAlign: 'center', color: '#fff' },
  startCameraBtn: {
    marginTop: '12px', padding: '8px 20px', background: T.primary, color: T.white,
    border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '500',
  },
  micTestCard: {
    background: T.white, borderRadius: '16px', border: `1px solid ${T.border}`,
    overflow: 'hidden', marginBottom: '24px', boxShadow: T.shadow,
  },
  micHeader: {
    padding: '16px 20px', background: '#F8FAFE', borderBottom: `1px solid ${T.border}`,
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  micTitle: { fontSize: '14px', fontWeight: '600', color: T.text },
  micStatus: { fontSize: '12px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '8px' },
  micContent: { padding: '20px' },
  micInstruction: { fontSize: '13px', color: T.textSecondary, marginBottom: '12px' },
  testPhrase: {
    background: '#EEF4FF', padding: '16px', borderRadius: '10px',
    fontSize: '15px', fontStyle: 'italic', color: T.primary, marginBottom: '16px', textAlign: 'center',
  },
  testMicBtn: {
    padding: '10px 20px', background: T.primary, color: T.white, border: 'none',
    borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '500', width: '100%',
  },
  micSuccess: {
    marginTop: '12px', padding: '10px', background: T.successBg,
    borderRadius: '8px', textAlign: 'center', fontSize: '13px', color: T.success,
  },
  consentCard: {
    background: T.white, borderRadius: '16px', border: `1px solid ${T.border}`,
    overflow: 'hidden', marginBottom: '24px', boxShadow: T.shadow,
  },
  consentHeader: { padding: '16px 20px', background: '#F8FAFE', borderBottom: `1px solid ${T.border}` },
  consentTitle: { fontSize: '14px', fontWeight: '600', color: T.text },
  consentContent: { padding: '20px' },
  checkboxLabel: {
    display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px', cursor: 'pointer',
  },
  checkbox: { width: '18px', height: '18px', marginTop: '2px', cursor: 'pointer' },
  checkboxText: { fontSize: '13px', color: T.textSecondary, lineHeight: '1.5' },
  linkText: { color: T.primary, fontWeight: '500', cursor: 'pointer', textDecoration: 'underline' },
  instructionsCard: {
    background: '#FFF8E1', borderRadius: '16px', border: `1px solid #FFE082`,
    overflow: 'hidden', marginBottom: '24px',
  },
  instructionsHeader: { padding: '16px 20px', background: '#FFF3E0', borderBottom: `1px solid #FFE082` },
  instructionsTitle: { fontSize: '14px', fontWeight: '600', color: '#E65100' },
  instructionsContent: { padding: '20px' },
  instructionsList: { listStyle: 'none', padding: 0, margin: 0 },
  continueSection: { textAlign: 'center', marginTop: '8px' },
  continueBtn: {
    padding: '14px 32px', fontSize: '16px', fontWeight: '600',
    background: T.primary, color: T.white, border: 'none', borderRadius: '10px',
    cursor: 'pointer', marginBottom: '16px',
  },
  waitingText: { fontSize: '12px', color: T.textMuted, textAlign: 'center' },
  statusDot: { width: '8px', height: '8px', borderRadius: '50%' },
};

/* ─── Modal Styles ─── */
const modalStyles = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 100000, padding: '20px',
  },
  box: {
    background: T.white, borderRadius: 20, width: '100%', maxWidth: 640,
    maxHeight: '85vh', display: 'flex', flexDirection: 'column',
    boxShadow: '0 24px 60px rgba(0,0,0,0.3)',
    overflow: 'hidden',
  },
  header: {
    padding: '20px 28px', borderBottom: `1px solid ${T.border}`,
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    background: '#F8FAFE', flexShrink: 0,
  },
  title: { fontSize: 18, fontWeight: 700, color: T.text, fontFamily: "'Poppins', sans-serif" },
  closeBtn: {
    width: 32, height: 32, borderRadius: '50%', border: `1px solid ${T.border}`,
    background: T.white, cursor: 'pointer', display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontSize: 18, color: T.textSecondary,
    transition: 'all 0.2s',
  },
  body: {
    padding: '24px 28px', overflowY: 'auto', flex: 1,
    lineHeight: 1.7, fontSize: 14, color: T.textSecondary,
  },
  footer: {
    padding: '16px 28px', borderTop: `1px solid ${T.border}`,
    display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0,
    background: T.white,
  },
  section: { marginBottom: 20, textAlign: 'left' },
  sectionTitle: { fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 8, textAlign: 'left' },
  list: { paddingLeft: 20, marginBottom: 0 },
  listItem: { marginBottom: 6, fontSize: 13, color: T.textSecondary, textAlign: 'left' },
  badge: {
    display: 'inline-block', padding: '2px 10px', borderRadius: 12, textAlign: 'left',
    fontSize: 11, fontWeight: 600, background: '#EEF4FF', color: T.primary, marginRight: 6,
  },
  highlight: {
    background: '#F0F4FF', border: `1px solid #C5D8FF`,
    borderRadius: 8, padding: '12px 16px', marginBottom: 12, fontSize: 13, color: '#1E3A8A',
  },
};

const POLICY_CONTENT = {
  terms: {
    icon: '📄',
    title: 'Terms and Conditions',
    sections: [
      {
        title: '1. Acceptance of Terms',
        content: 'By accessing and participating in this AI Interview process, you ("Candidate") acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions. If you do not agree, please do not proceed with the interview.',
      },
      {
        title: '2. Interview Process',
        content: 'This interview is conducted using AI-powered technology. Your responses, both text and audio/video, will be recorded and analyzed. The interview process is designed to be fair and unbiased. Results will be used solely for recruitment evaluation purposes.',
        bullets: [
          'The interview session will be recorded in full.',
          'AI analysis will evaluate your responses for relevance and quality.',
          'Results are confidential and shared only with the hiring organization.',
          'You may not share interview questions externally.',
        ],
      },
      {
        title: '3. Candidate Obligations',
        content: 'You agree to:',
        bullets: [
          'Provide truthful and accurate responses throughout the interview.',
          'Not use external assistance, notes, or AI tools during the interview.',
          'Remain in a quiet, well-lit environment for the duration.',
          'Not share interview content or questions with others.',
          'Maintain appropriate conduct throughout the session.',
        ],
      },
      {
        title: '4. Intellectual Property',
        content: 'All interview questions, evaluation frameworks, and AI models used in this process are proprietary to Humanex. You retain ownership of your personal responses, but grant Humanex a limited license to analyze and store them for recruitment purposes.',
      },
      {
        title: '5. Termination',
        content: 'Humanex reserves the right to terminate the interview session if a candidate is found to be violating these terms, including but not limited to switching tabs, using unauthorized assistance, or engaging in misconduct.',
      },
      {
        title: '6. Limitation of Liability',
        content: 'Humanex shall not be liable for any technical issues beyond our reasonable control that affect the interview session. In such cases, candidates will be given the opportunity to reschedule.',
      },
    ],
  },
  privacy: {
    icon: '🔒',
    title: 'Privacy Policy',
    sections: [
      {
        title: '1. Information We Collect',
        content: 'During the interview process, we collect the following personal data:',
        bullets: [
          'Identity information: Full name, email address, phone number.',
          'Interview data: Audio and video recordings of your responses.',
          'Technical data: IP address, browser type, device information.',
          'Biometric data: Facial recognition data for identity verification.',
          'Assessment data: Scores, timestamps, and behavioral patterns.',
        ],
      },
      {
        title: '2. How We Use Your Data',
        content: 'Your personal data is used strictly for:',
        bullets: [
          'Evaluating your suitability for the applied position.',
          'Identity verification and anti-fraud measures.',
          'Improving our AI interview and assessment tools.',
          'Complying with legal and regulatory obligations.',
        ],
      },
      {
        title: '3. Data Retention',
        content: 'We retain your interview data for a period of 12 months from the date of your interview, after which it is securely deleted. Successful candidates\' data may be retained as part of their employment record in accordance with applicable law.',
      },
      {
        title: '4. Data Sharing',
        content: 'We do not sell your personal data. We may share your data with:',
        bullets: [
          'The hiring organization for which you applied.',
          'Technology partners who assist in operating our platform (under strict data processing agreements).',
          'Law enforcement or regulatory authorities when required by law.',
        ],
      },
      {
        title: '5. Your Rights',
        content: 'Under applicable data protection laws, you have the right to:',
        bullets: [
          'Access the personal data we hold about you.',
          'Request correction of inaccurate data.',
          'Request deletion of your data (subject to legal obligations).',
          'Object to or restrict processing of your data.',
          'Data portability — receive your data in a structured format.',
        ],
      },
      {
        title: '6. Security Measures',
        content: 'We implement industry-standard security measures including AES-256 encryption for data at rest, TLS 1.3 for data in transit, access controls and audit logging, and regular security assessments and penetration testing.',
      },
      {
        title: '7. Contact Us',
        content: 'For any privacy-related queries or to exercise your rights, contact our Data Protection Officer at privacy@humanex.ai or write to: Humanex Privacy Team, [Address].',
      },
    ],
  },
  ai: {
    icon: '🤖',
    title: 'AI Processing Consent',
    sections: [
      {
        title: 'What is AI Processing?',
        content: 'Our platform uses advanced artificial intelligence to analyze your interview responses. This helps us provide fair, consistent, and objective evaluations across all candidates.',
      },
      {
        title: 'What AI Analyzes',
        content: 'Our AI systems evaluate:',
        bullets: [
          'Content relevance and depth of your answers.',
          'Communication clarity and structured thinking.',
          'Technical accuracy for role-specific questions.',
          'Response timing and engagement patterns.',
          'Facial expression and tone analysis (where permitted by law).',
        ],
      },
      {
        title: 'Fairness & Bias Mitigation',
        content: 'We are committed to fair AI evaluation. Our models are regularly audited for bias and comply with applicable AI fairness guidelines. Human reviewers oversee all final hiring decisions — AI scores are one input among many.',
      },
      {
        title: 'Your Rights Regarding AI',
        content: 'You have the right to:',
        bullets: [
          'Request human review of any AI-generated assessment.',
          'Understand how your score was calculated (explainability).',
          'Contest an AI decision you believe is incorrect.',
          'Opt out of AI analysis (this may limit your ability to proceed with the digital interview format).',
        ],
      },
    ],
  },
  recording: {
    icon: '🎥',
    title: 'Recording Consent',
    sections: [
      {
        title: 'What Will Be Recorded',
        content: 'By proceeding with this interview, you consent to the following being recorded:',
        bullets: [
          'Your video feed throughout the interview session.',
          'Your audio responses to all interview questions.',
          'Your screen activity during assessment sections.',
          'Proctoring data including eye movement and tab activity.',
        ],
      },
      {
        title: 'Purpose of Recording',
        content: 'Recordings are used to:',
        bullets: [
          'Allow recruiters and hiring managers to review your interview.',
          'Ensure integrity of the assessment process.',
          'Provide AI analysis of your responses.',
          'Resolve any disputes regarding interview conduct.',
        ],
      },
      {
        title: 'Storage & Security',
        content: 'All recordings are stored on secure, encrypted servers. Access is restricted to authorized personnel and the hiring organization. Recordings are retained for 12 months and then permanently deleted.',
      },
      {
        title: 'Withdrawal of Consent',
        content: 'You may withdraw recording consent at any time by ending the interview. However, please note that withdrawal will terminate your participation in this digital interview process. You may contact the hiring organization to explore alternative interview formats.',
      },
    ],
  },
};

const generateCaptcha = () => {
  const c = 'ABCDEFGHJKLMNPQRSTUVWXYZ123456789';
  return Array.from({ length: 5 }, () => c[Math.floor(Math.random() * c.length)]).join('');
};

/* ─── Policy Modal ─── */
const PolicyModal = ({ type, onClose, onAccept }) => {
  const [scrolled, setScrolled] = useState(false);
  const bodyRef = useRef(null);
  const content = POLICY_CONTENT[type];
  if (!content) return null;

  const handleScroll = (e) => {
    const el = e.target;
    const bottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 40;
    if (bottom) setScrolled(true);
  };

  return (
    <div className="modal-overlay" style={modalStyles.overlay} onClick={onClose}>
      <div className="modal-box" style={modalStyles.box} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={modalStyles.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 22 }}>{content.icon}</span>
            <span style={modalStyles.title}>{content.title}</span>
          </div>
          <button style={modalStyles.closeBtn} onClick={onClose}>×</button>
        </div>

        {/* Body */}
        <div ref={bodyRef} style={modalStyles.body} onScroll={handleScroll}>
          <div style={{ ...modalStyles.highlight, marginBottom: 20 }}>
            Please read this document carefully. Scroll to the bottom to enable the accept button.
          </div>
          {content.sections.map((sec, i) => (
            <div key={i} style={modalStyles.section}>
              <div style={modalStyles.sectionTitle}>{sec.title}</div>
              <p style={{ fontSize: 13, color: T.textSecondary, marginBottom: sec.bullets ? 10 : 0, lineHeight: 1.7 }}>{sec.content}</p>
              {sec.bullets && (
                <ul style={modalStyles.list}>
                  {sec.bullets.map((b, j) => (
                    <li key={j} style={modalStyles.listItem}>{b}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
          <div style={{ height: 1 }} /> {/* scroll sentinel */}
        </div>

        {/* Footer */}
        <div style={modalStyles.footer}>
          <button className="btn-outline" style={S.btnOutline} onClick={onClose}>Close</button>
          {onAccept && (
            <button
              className="btn-primary"
              style={{
                ...S.btnPrimary,
                opacity: scrolled ? 1 : 0.5,
                cursor: scrolled ? 'pointer' : 'not-allowed',
              }}
              onClick={() => { if (scrolled) { onAccept(); onClose(); } }}
            >
              ✓ I Accept
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── Small Components ─── */
const StepTag = ({ n, total = 9 }) => (
  <div style={S.stepTag}>
    <span style={S.stepTagDot} />
    Step {n} of {total}
  </div>
);

const InfoBox = ({ children }) => (
  <div style={S.infoBox}>
    <span style={S.infoIcon}>ℹ️</span>
    <p style={S.infoText}>{children}</p>
  </div>
);

const Spinner = ({ size = 36, color = T.primary }) => (
  <div style={{
    width: size, height: size, borderRadius: '50%',
    border: `3px solid ${color}22`,
    borderTop: `3px solid ${color}`,
    animation: 'spin 0.8s linear infinite',
    flexShrink: 0,
  }} />
);

/* ─── Time Restriction Modal ─── */
const TimeRestrictionModal = ({ data, onClose }) => {
  const [countdown, setCountdown] = useState(null);
  useEffect(() => {
    if (data?.canAccessAt) {
      const target = new Date(data.canAccessAt);
      const tick = () => {
        const diff = target - new Date();
        if (diff <= 0) { setCountdown('Available now'); return; }
        const h = Math.floor(diff / 3600000);
        const m = Math.floor((diff % 3600000) / 60000);
        const s = Math.floor((diff % 60000) / 1000);
        setCountdown(`${h}h ${m}m ${s}s`);
      };
      tick();
      const id = setInterval(tick, 1000);
      return () => clearInterval(id);
    }
  }, [data]);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100000 }}>
      <div style={{ background: T.white, borderRadius: 20, padding: 40, maxWidth: 460, width: '90%', textAlign: 'center', boxShadow: S.shadowLg }}>
        <div style={{ fontSize: 56, marginBottom: 16 }}>⏰</div>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: T.text, marginBottom: 8, fontFamily: "'Poppins', sans-serif" }}>Interview Not Started Yet</h2>
        <p style={{ color: T.textSecondary, marginBottom: 24, fontSize: 14 }}>Your interview is scheduled for a future time. Please check back later.</p>
        <div style={{ background: T.bg, borderRadius: 12, padding: 20, marginBottom: 24, textAlign: 'left' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: `1px solid ${T.border}` }}>
            <span style={{ color: T.textSecondary, fontSize: 13 }}>Scheduled Date</span>
            <span style={{ fontWeight: 600, fontSize: 13 }}>{data?.scheduledDate}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0' }}>
            <span style={{ color: T.textSecondary, fontSize: 13 }}>Available From</span>
            <span style={{ fontWeight: 600, fontSize: 13, color: T.primary }}>{new Date(data?.canAccessAt).toLocaleString()}</span>
          </div>
        </div>
        {countdown && countdown !== 'Available now' && (
          <div style={{ background: '#EEF4FF', borderRadius: 10, padding: '14px 20px', marginBottom: 24 }}>
            <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 4 }}>Available in</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: T.primary, fontFamily: 'monospace' }}>{countdown}</div>
          </div>
        )}
        <button className="btn-primary" style={S.btnPrimary} onClick={onClose}>Close</button>
      </div>
    </div>
  );
};

/* ─── Kiosk Interview Mode ─── */
const KioskInterviewMode = ({ meetingUrl, onComplete, interviewId, applicationId }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [tabViolationCount, setTabViolationCount] = useState(0);
  const [showLockdownWarning, setShowLockdownWarning] = useState(false);
  const iframeRef = useRef(null);
  const fullscreenAttempts = useRef(0);
  const maxTabViolations = 3;

  const enterFullscreen = async () => {
    try {
      const el = document.documentElement;
      if (el.requestFullscreen) await el.requestFullscreen();
      else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
    } catch {}
  };

  useEffect(() => {
    setTimeout(() => enterFullscreen(), 1000);
    const onFSChange = () => {
      const isFS = !!document.fullscreenElement;
      setIsFullscreen(isFS);
      if (!isFS && !showExitConfirm && !showFeedback && !showThankYou) {
        if (fullscreenAttempts.current < 5) { fullscreenAttempts.current++; setTimeout(() => enterFullscreen(), 500); }
        else { confirmEndCall(); }
      } else { fullscreenAttempts.current = 0; }
    };
    document.addEventListener('fullscreenchange', onFSChange);
    const onVisibility = () => {
      if (document.hidden) {
        setTabViolationCount(v => {
          const n = v + 1;
          setShowLockdownWarning(true);
          if (n >= maxTabViolations) setTimeout(() => confirmEndCall(), 1000);
          else setTimeout(() => setShowLockdownWarning(false), 3000);
          return n;
        });
      }
    };
    const onBlur = () => { window.focus(); };
    const blockKeys = (e) => {
      if ((e.ctrlKey && 'tnwTNW'.includes(e.key)) || e.key === 'F5' || (e.ctrlKey && 'rR'.includes(e.key)) || e.key === 'Escape') e.preventDefault();
    };
    const blockMenu = (e) => e.preventDefault();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', onBlur);
    document.addEventListener('keydown', blockKeys);
    document.addEventListener('contextmenu', blockMenu);
    return () => {
      document.removeEventListener('fullscreenchange', onFSChange);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('keydown', blockKeys);
      document.removeEventListener('contextmenu', blockMenu);
      if (document.exitFullscreen) document.exitFullscreen().catch(() => {});
    };
  }, []);

  const confirmEndCall = async () => {
    try { if (document.exitFullscreen) await document.exitFullscreen(); } catch {}
    setShowExitConfirm(false);
    setShowFeedback(true);
  };

  if (showFeedback) return <InterviewFeedbackForm interviewId={interviewId} applicationId={applicationId} onClose={() => setShowFeedback(false)} onComplete={() => { setShowFeedback(false); setShowThankYou(true); }} />;
  if (showThankYou) return <ThankYouMessage onComplete={() => { setShowThankYou(false); if (onComplete) onComplete(); }} />;

  return (
    <div style={S.kioskContainer}>
      {showLockdownWarning && <div style={S.lockdownWarning}>⚠️ Tab switching is NOT allowed! Violation {tabViolationCount}/{maxTabViolations}</div>}
      <div style={S.kioskHeader}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: '#EF4444', fontWeight: 600 }}>🔴 Secure Mode Active</span>
          {tabViolationCount > 0 && <span style={{ fontSize: 11, color: '#FCD34D' }}>⚠️ Warnings: {tabViolationCount}/{maxTabViolations}</span>}
          {!isFullscreen && <button onClick={enterFullscreen} style={{ background: T.accent, color: '#fff', border: 'none', padding: '4px 12px', borderRadius: '4px', fontSize: '11px', cursor: 'pointer', fontWeight: 600 }}>Enter Fullscreen</button>}
        </div>
        <button style={S.kioskEndCallBtn} onClick={() => setShowExitConfirm(true)}>End Interview</button>
      </div>
      {isLoading && (
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center', color: 'white' }}>
          <Spinner color="#fff" /><p style={{ marginTop: 12 }}>Starting secure interview...</p>
        </div>
      )}
      <iframe ref={iframeRef} src={meetingUrl} style={S.kioskIframe} allow="camera; microphone; display-capture; autoplay; fullscreen" onLoad={() => setIsLoading(false)} title="Secure Interview" />
      {showExitConfirm && (
        <div style={S.exitOverlay}>
          <div style={S.exitBox}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>🚪</div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 10 }}>End Interview?</h3>
            <p style={{ color: T.textSecondary, fontSize: 14, marginBottom: 24 }}>This action cannot be undone. Are you sure you want to end this interview?</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button style={{ ...S.btnPrimary, background: '#DC2626' }} onClick={confirmEndCall}>Yes, End Interview</button>
              <button className="btn-outline" style={S.btnOutline} onClick={() => { setShowExitConfirm(false); enterFullscreen(); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Assessment Mode ─── */
const AssessmentMode = ({ assessmentId, applicationId, candidateInfo, onComplete }) => {
  const [assessment, setAssessment] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [violationCount, setViolationCount] = useState(0);
  const [warnings, setWarnings] = useState([]);
  const [submissionId, setSubmissionId] = useState(null);
  const [showLockdownWarning, setShowLockdownWarning] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const maxViolations = 3;

  const addWarning = (message) => setWarnings(prev => [{ id: Date.now(), message, time: new Date().toLocaleTimeString() }, ...prev.slice(0, 4)]);

  useEffect(() => {
    loadAssessment();
    enableCamera();
    const onVisibility = () => {
      if (document.hidden) {
        setViolationCount(v => {
          const n = v + 1;
          setShowLockdownWarning(true);
          addWarning(`Tab switch detected (${n}/${maxViolations})`);
          if (n >= maxViolations) setTimeout(() => handleSubmit(), 1000);
          else setTimeout(() => setShowLockdownWarning(false), 3000);
          return n;
        });
      }
    };
    const blockKeys = (e) => {
      if ((e.ctrlKey && 'tnwTNWrR'.includes(e.key)) || 'F5F12'.includes(e.key) || (e.ctrlKey && e.shiftKey && 'IC'.includes(e.key))) {
        e.preventDefault(); addWarning('Shortcut blocked');
      }
    };
    const blockMenu = (e) => e.preventDefault();
    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('keydown', blockKeys);
    document.addEventListener('contextmenu', blockMenu);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('keydown', blockKeys);
      document.removeEventListener('contextmenu', blockMenu);
      stopCamera();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const enableCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch {}
  };
  const stopCamera = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
  };

  const loadAssessment = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE_URL}/api/assessments/${assessmentId}`, {
        params: { candidateEmail: candidateInfo.email, candidateName: candidateInfo.fullName, candidateMobile: candidateInfo.mobile, applicationId }
      });
      if (res.data.success) {
        const { assessment: a, questions: q, submission: sub } = res.data.data;
        if (sub?.status === 'submitted') { setError('Already submitted.'); setIsSubmitted(true); setLoading(false); return; }
        setAssessment(a); setQuestions(q || []);
        setSubmissionId(sub?.submissionId);
        setTimeLeft(a?.timeRemaining || (a?.duration || 60) * 60);
        if (q?.length > 0) startTimer();
        else setError('No questions found.');
      } else { setError(res.data.message || 'Failed to load assessment'); }
    } catch (err) {
      if (err.response?.status === 409) { setError('Already submitted.'); setIsSubmitted(true); }
      else if (err.response?.status === 403) setError('Assessment not available.');
      else setError('Failed to load. Please try again.');
    } finally { setLoading(false); }
  };

  const startTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimeLeft(p => { if (p <= 1) { clearInterval(timerRef.current); handleSubmit(); return 0; } return p - 1; });
    }, 1000);
  };

  const formatTime = (s) => `${String(Math.floor(s / 3600)).padStart(2, '0')}:${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  const handleSubmit = async () => {
    if (submitting) return;
    if (!window.confirm('Submit assessment? You cannot change answers after this.')) return;
    try {
      setSubmitting(true);
      const res = await axios.post(`${API_BASE_URL}/api/assessments/${assessmentId}/submit`, {
        candidateEmail: candidateInfo.email, candidateName: candidateInfo.fullName,
        candidateMobile: candidateInfo.mobile, answers,
        timeSpent: (assessment?.duration || 60) * 60 - timeLeft,
        proctoringData: { tabSwitches: violationCount, totalViolations: violationCount, warnings },
        submissionId, applicationId,
      });
      if (res.data.success) { stopCamera(); setIsSubmitted(true); setSubmissionId(res.data.data.submissionId); if (timerRef.current) clearInterval(timerRef.current); }
    } catch (err) { alert(err.response?.data?.message || 'Submission failed.'); }
    finally { setSubmitting(false); }
  };

  const answeredCount = Object.keys(answers).length;
  const progressPct = questions.length > 0 ? (answeredCount / questions.length) * 100 : 0;

  const containerStyle = {
    position: 'fixed', inset: 0, background: T.bg, zIndex: 99999,
    display: 'flex', flexDirection: 'column', overflow: 'auto', fontFamily: "'Inter', sans-serif",
  };
  const headerStyle = {
    background: T.white, padding: '12px 28px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    borderBottom: `1px solid ${T.border}`, boxShadow: T.shadow, flexShrink: 0,
  };

  if (loading) return <div style={containerStyle}><div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}><Spinner /><p style={{ color: T.textSecondary }}>Loading assessment...</p></div></div>;

  if (error && !isSubmitted) return (
    <div style={containerStyle}>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 48 }}>⚠️</div>
        <h2 style={{ color: '#DC2626', fontSize: 20 }}>Error</h2>
        <p style={{ color: T.textSecondary }}>{error}</p>
        <button className="btn-primary" style={S.btnPrimary} onClick={() => window.close()}>Close</button>
      </div>
    </div>
  );

  if (isSubmitted) return (
    <div style={containerStyle}>
      <div style={headerStyle}><span style={{ fontSize: 16, fontWeight: 700, color: T.text }}>Assessment {submissionId ? 'Submitted' : 'Already Taken'}</span></div>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
        <div style={{ textAlign: 'center', maxWidth: 480, background: T.white, borderRadius: 16, padding: 48, boxShadow: S.shadowMd }}>
          <div style={{ fontSize: 56, marginBottom: 20 }}>{submissionId ? '✅' : '⚠️'}</div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: T.text, marginBottom: 10, fontFamily: "'Poppins', sans-serif" }}>{submissionId ? 'Assessment Submitted!' : 'Already Submitted'}</h2>
          <p style={{ color: T.textSecondary, marginBottom: 28, lineHeight: 1.6 }}>{submissionId ? 'Thank you for completing the assessment. We will review your responses and contact you soon.' : 'You have already taken this assessment.'}</p>
          <button className="btn-primary" style={S.btnPrimary} onClick={() => window.close()}>Close Window</button>
        </div>
      </div>
    </div>
  );

  if (!assessment || questions.length === 0) return null;
  const q = questions[currentQuestion];
  if (!q) return null;

  return (
    <div style={containerStyle}>
      {showLockdownWarning && <div style={S.lockdownWarning}>⚠️ Tab switching is NOT allowed! Violation {violationCount}/{maxViolations}</div>}
      <div style={headerStyle}>
        <span style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: "'Poppins', sans-serif" }}>{assessment.title}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ background: T.successBg, padding: '5px 14px', borderRadius: 20, fontSize: 12, color: T.success, fontWeight: 600 }}>{candidateInfo.fullName}</span>
          <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 8, padding: '6px 16px', color: '#DC2626', fontWeight: 700, fontSize: 16, fontFamily: 'monospace' }}>{formatTime(timeLeft)}</div>
        </div>
      </div>
      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 280px', overflow: 'hidden' }}>
        <div style={{ overflowY: 'auto', padding: 28 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
            {questions.map((_, idx) => {
              const isCur = currentQuestion === idx;
              const isAns = !!answers[questions[idx]._id];
              return (
                <button key={idx} onClick={() => setCurrentQuestion(idx)} style={{
                  width: 34, height: 34, borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 12,
                  background: isCur ? T.primary : isAns ? T.successBg : T.border,
                  color: isCur ? '#fff' : isAns ? T.success : T.textSecondary,
                  outline: isAns && !isCur ? `1.5px solid ${T.successBdr}` : 'none',
                }}>{idx + 1}</button>
              );
            })}
          </div>
          <div style={{ height: 4, background: T.border, borderRadius: 4, marginBottom: 20, overflow: 'hidden' }}>
            <div style={{ height: '100%', background: T.success, borderRadius: 4, width: `${progressPct}%`, transition: 'width 0.3s' }} />
          </div>
          <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden', boxShadow: T.shadow }}>
            <div style={{ padding: '18px 24px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: T.text }}>Question {currentQuestion + 1} / {questions.length}</span>
              <span style={{ background: '#EEF4FF', padding: '4px 12px', borderRadius: 20, fontSize: 11, color: T.primary, fontWeight: 600 }}>{q.type?.replace('_', ' ')}</span>
            </div>
            <div style={{ padding: 28 }}>
              <p style={{ fontSize: 15, color: T.text, lineHeight: 1.7, marginBottom: 24 }}>{q.question}</p>
              {q.type === 'multiple_choice' && q.options && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {q.options.map((opt, idx) => {
                    const selected = answers[q._id] === opt;
                    return (
                      <div key={idx} className="option-card" onClick={() => setAnswers(p => ({ ...p, [q._id]: opt }))} style={{
                        display: 'flex', alignItems: 'center', gap: 14,
                        background: selected ? '#EEF4FF' : T.white,
                        border: `1.5px solid ${selected ? T.primary : T.border}`,
                        borderRadius: 10, padding: '14px 18px', cursor: 'pointer', transition: 'all 0.2s',
                      }}>
                        <div style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${selected ? T.primary : T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          {selected && <div style={{ width: 8, height: 8, borderRadius: '50%', background: T.primary }} />}
                        </div>
                        <span style={{ fontSize: 14, color: T.text }}>{opt}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              {['text', 'scenario', 'case_study', 'coding', 'sql', 'debugging', 'system_design'].includes(q.type) && (
                <textarea value={answers[q._id] || ''} onChange={e => setAnswers(p => ({ ...p, [q._id]: e.target.value }))}
                  placeholder="Type your answer here..."
                  style={{ width: '100%', minHeight: 180, background: T.bg, border: `1.5px solid ${T.border}`, borderRadius: 10, padding: 16, color: T.text, fontSize: 14, fontFamily: 'inherit', resize: 'vertical', outline: 'none' }} />
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24, gap: 12 }}>
                <button className="btn-outline" style={S.btnOutline} disabled={currentQuestion === 0} onClick={() => setCurrentQuestion(p => p - 1)}>← Previous</button>
                {currentQuestion === questions.length - 1
                  ? <button style={{ ...S.btnPrimary, background: '#DC2626' }} disabled={submitting} onClick={handleSubmit}>{submitting ? 'Submitting...' : 'Submit Assessment'}</button>
                  : <button className="btn-primary" style={S.btnPrimary} onClick={() => setCurrentQuestion(p => p + 1)}>Next →</button>}
              </div>
            </div>
          </div>
        </div>
        <div style={{ background: T.white, borderLeft: `1px solid ${T.border}`, padding: 20, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 10 }}>Live Monitoring</div>
            <div style={{ background: '#111', borderRadius: 10, overflow: 'hidden', aspectRatio: '4/3' }}>
              <video ref={videoRef} autoPlay muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
          </div>
          <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 10 }}>Progress</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 13, color: T.textSecondary }}>Answered</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: T.success }}>{answeredCount}/{questions.length}</span>
            </div>
            <div style={{ height: 6, background: T.border, borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ height: '100%', background: T.success, borderRadius: 4, width: `${progressPct}%`, transition: 'width 0.3s' }} />
            </div>
          </div>
          {violationCount > 0 && (
            <div style={{ background: '#FFF8E1', border: `1px solid #FFE082`, borderRadius: 10, padding: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#92400E', marginBottom: 6 }}>⚠ Warnings: {violationCount}/{maxViolations}</div>
              {warnings.slice(0, 3).map(w => <div key={w.id} style={{ fontSize: 11, color: '#92400E', marginBottom: 4 }}>{w.message}</div>)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
const InterviewPrerequisites = () => {
  const { interviewId } = useParams();
  const navigate = useNavigate();
  const [token] = useState(new URLSearchParams(window.location.search).get('token'));

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [showKioskMode, setShowKioskMode] = useState(false);
  const [showAssessmentMode, setShowAssessmentMode] = useState(false);
  const [showAIMode, setShowAIMode] = useState(false);
  const [meetingUrl, setMeetingUrl] = useState('');
  const [showTimeRestrictionModal, setShowTimeRestrictionModal] = useState(false);
  const [timeRestrictionData, setTimeRestrictionData] = useState(null);
  const [currentInterviewId, setCurrentInterviewId] = useState(null);
  const [currentApplicationId, setCurrentApplicationId] = useState(null);
  const [isAssessmentMode, setIsAssessmentMode] = useState(false);
  const [assessmentId, setAssessmentId] = useState(null);

  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [cameraActive, setCameraActive] = useState(false); // ✅ NEW: tracks camera state reactively
  const [systemCheckStatus, setSystemCheckStatus] = useState({ internet: false, camera: false, microphone: false, browser: true, checking: false });
  const [otp, setOtp] = useState('');
  const [captcha, setCaptcha] = useState('');
  const [captchaText, setCaptchaText] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [loadingOtp, setLoadingOtp] = useState(false);
  const [timer, setTimer] = useState(0);
  const [candidateInfo, setCandidateInfo] = useState({ fullName: '', email: '', mobile: '' });

  // ✅ Consent states (replaces DOM getElementById)
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [aiConsent, setAiConsent] = useState(false);
  const [recordingConsent, setRecordingConsent] = useState(false);

  // ✅ Modal state
  const [activeModal, setActiveModal] = useState(null); // 'terms' | 'privacy' | 'ai' | 'recording' | null

  const timerRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const cameraStartedRef = useRef(false); // ✅ prevents double-start on re-render

  const showNotification = useCallback((type, message) => {
    // Handle legacy call signature: showNotification('message text', 'success')
    let msgText = message;
    let msgType = type;
    if (type && !['success', 'error', 'info'].includes(type)) {
      msgText = type;
      msgType = message || 'info';
    }
    const el = document.createElement('div');
    el.textContent = msgText;
    Object.assign(el.style, {
      position: 'fixed', top: '20px', right: '20px', zIndex: '999999',
      padding: '12px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '500',
      background: msgType === 'success' ? '#E8F5E9' : msgType === 'error' ? '#FFEBEE' : '#EEF4FF',
      color: msgType === 'success' ? '#1B5E20' : msgType === 'error' ? '#B71C1C' : '#0D47A1',
      border: `1px solid ${msgType === 'success' ? '#A5D6A7' : msgType === 'error' ? '#EF9A9A' : '#90CAF9'}`,
      boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
      animation: 'slideIn 0.3s ease', fontFamily: "'Inter', sans-serif",
      maxWidth: '360px',
    });
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 3500);
  }, []);

  useEffect(() => { setCaptchaText(generateCaptcha()); }, []);
  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);
  useEffect(() => { loadPrerequisites(); }, [interviewId, token]);

  // ✅ FIXED: Camera auto-start for step 6 — uses ref to prevent re-triggering on every render
  useEffect(() => {
    if (currentStep === 6 && !capturedPhoto && !cameraStartedRef.current) {
      cameraStartedRef.current = true;
      startCamera();
    }
    if (currentStep !== 6) {
      cameraStartedRef.current = false;
    }
  }, [currentStep]); // ✅ Only depend on currentStep, NOT capturedPhoto

  const loadPrerequisites = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE_URL}/api/applications/interview/${interviewId}/prerequisites?token=${token}`);
      if (res.data.message === 'completed') { showNotification('info', res.data.error || 'Interview already completed.'); setTimeout(() => navigate('/'), 3000); return; }
      if (res.data.message === 'not_yet_accessible') { setShowTimeRestrictionModal(true); setTimeRestrictionData(res.data.data); setLoading(false); return; }
      if (res.data.success) {
        const d = res.data.data;
        setData(d);
        const isAss = d.interview?.isAssessment === true || d.interview?.interviewType === 'Assessment';
        setIsAssessmentMode(isAss);
        const appId = d.application?._id || d.application?.id || d._id;
        if (appId) setCurrentApplicationId(appId);
        if (isAss && d.interview?.assessmentId) setAssessmentId(d.interview.assessmentId);
        const app = d.application || {};
        setCandidateInfo({
          fullName: app.name || app.fullName || app.candidateName || '',
          email: app.email || app.emailId || '',
          mobile: app.phone || app.mobile || app.mobileNumber || app.phoneNumber || '',
        });
        const first = (d.prerequisites?.steps || []).findIndex(s => !s.isCompleted) + 1;
        setCurrentStep(first || 1);
      }
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.message === 'not_yet_accessible') { setShowTimeRestrictionModal(true); setTimeRestrictionData(err.response.data.data); }
      else if (err.response?.data?.message === 'completed') { showNotification('info', 'Interview already completed.'); setTimeout(() => navigate('/'), 3000); }
      else showNotification('error', err.response?.data?.message || 'Failed to load prerequisites');
    } finally { setLoading(false); }
  };

  const startInterview = async () => {
    try {
      const res = await axios.post(`${API_BASE_URL}/api/applications/interview/${interviewId}/start`);
      if (res.data.success) {
        const isAss = res.data.data?.isAssessment || isAssessmentMode;
        const isAI = res.data.data?.interviewType === 'AI Interview' || data?.interview?.interviewType === 'AI Interview';
        const link = res.data.data?.meetingLink;
        const aId = res.data.data?.assessmentId || assessmentId;

        if (isAI) { setShowAIMode(true); return; }
        if (isAss && aId) { setShowAssessmentMode(true); return; }
        if (link) {
          const isJitsi = link.includes('meet.jit.si');
          if (isJitsi) { setMeetingUrl(link); setCurrentInterviewId(interviewId); setShowKioskMode(true); }
          else { window.location.href = link; }
        } else { showNotification('error', 'No meeting link found.'); }
      }
    } catch (e) { showNotification('error', e.response?.data?.message || 'Failed to start interview'); }
  };

  // ✅ FIXED: Returns a promise, updates reactive state
  const startCamera = async () => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      setCameraActive(true); // ✅ reactive state update
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => {});
      }
      setSystemCheckStatus(prev => ({ ...prev, camera: true, microphone: true }));
      showNotification('success', 'Camera and microphone connected successfully!');
      return true;
    } catch (error) {
      console.error('Camera error:', error);
      setCameraActive(false);
      showNotification('error', 'Please allow camera and microphone access to continue');
      return false;
    }
  };

  const updateStep = async (stepNumber, stepData, type) => {
    try {
      const res = await axios.post(`${API_BASE_URL}/api/applications/interview/${interviewId}/prerequisite`, { stepNumber, stepData, type });
      if (res.data.success) {
        showNotification('success', `Step ${stepNumber} completed!`);
        if (res.data.allCompleted) startInterview();
        else { setCurrentStep(stepNumber + 1); loadPrerequisites(); }
      }
    } catch (e) { showNotification('error', e.response?.data?.message || 'Failed to update step'); }
  };

  const runSystemCheck = async () => {
    setSystemCheckStatus(p => ({ ...p, checking: true }));
    let internet = false;
    try { await fetch('https://www.google.com/favicon.ico', { method: 'HEAD', cache: 'no-cache' }); internet = true; } catch {}
    if (!internet) internet = navigator.onLine;
    let camera = false, mic = false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      camera = stream.getVideoTracks().length > 0;
      mic = stream.getAudioTracks().length > 0;
      if (videoRef.current) { videoRef.current.srcObject = stream; streamRef.current = stream; }
      setCameraActive(camera);
    } catch { showNotification('error', 'Please allow camera & microphone access.'); }
    const browser = !!(navigator.mediaDevices?.getUserMedia && window.RTCPeerConnection);
    const status = { internet, camera, microphone: mic, browser, checking: false };
    setSystemCheckStatus(status);
    if (internet && camera && mic && browser) { showNotification('success', 'System check passed!'); await updateStep(5, { passed: true }, 'system_check'); }
    else {
      const errs = []; if (!internet) errs.push('Internet'); if (!camera) errs.push('Camera'); if (!mic) errs.push('Microphone');
      showNotification('error', 'Failed: ' + errs.join(', '));
    }
  };

  const capturePhoto = async () => {
    // ✅ FIXED: Always try to start camera if stream is dead
    if (!streamRef.current) {
      const ok = await startCamera();
      if (!ok) return;
      // Give video time to initialize
      await new Promise(r => setTimeout(r, 800));
    }
    const video = videoRef.current;
    if (!video) { showNotification('error', 'Video element not found'); return; }
    await new Promise(r => {
      if (video.readyState >= 2) r();
      else { video.onloadeddata = r; setTimeout(r, 3000); } // safety timeout
    });
    if (!video.videoWidth) { showNotification('error', 'Camera not ready, please try again'); return; }
    try {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth; canvas.height = video.videoHeight;
      canvas.getContext('2d').drawImage(video, 0, 0);
      const photo = canvas.toDataURL('image/jpeg', 0.8);
      setCapturedPhoto(photo);
      // ✅ Stop stream after capture to free resources
      streamRef.current?.getTracks().forEach(t => t.stop());
      streamRef.current = null;
      setCameraActive(false);
      await updateStep(6, { photoData: photo, photoUrl: photo, capturedAt: new Date().toISOString() }, 'photo_verification');
      showNotification('success', 'Photo captured successfully!');
    } catch { showNotification('error', 'Capture failed. Please try again.'); }
  };

  const retakePhoto = useCallback(async () => {
    setCapturedPhoto(null);
    cameraStartedRef.current = false; // ✅ allow re-start
    await startCamera();
  }, []);

  const sendOTP = async () => {
    setLoadingOtp(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/assessments/send-otp`, { email: data?.application?.email });
      if (res.data.success) {
        setOtpSent(true); setTimer(300); showNotification('success', 'OTP sent!');
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = setInterval(() => setTimer(p => { if (p <= 1) { clearInterval(timerRef.current); return 0; } return p - 1; }), 1000);
      }
    } catch { showNotification('error', 'Failed to send OTP'); }
    finally { setLoadingOtp(false); }
  };

  const verifyOTP = async () => {
    if (!otp || otp.length !== 6) { showNotification('error', 'Enter complete OTP'); return; }
    if (captcha !== captchaText) { showNotification('error', 'Invalid CAPTCHA'); setCaptchaText(generateCaptcha()); setCaptcha(''); return; }
    setLoadingOtp(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/assessments/verify-otp`, { email: data?.application?.email, otp });
      if (res.data.success) { showNotification('success', 'OTP verified!'); await updateStep(3, { mobileVerified: true, emailVerified: true }, 'otp_verification'); }
      else showNotification('error', 'Invalid OTP');
    } catch { showNotification('error', 'Invalid OTP'); }
    finally { setLoadingOtp(false); }
  };

  /* ── Step data ── */
  const STEP_LABELS = ['Email Verification', 'Disclaimer', 'OTP Verification', 'Review Details', 'System Check', 'Photo Capture', 'Mic Test', 'Camera & Consent', 'Start Interview'];
  const steps = data?.prerequisites?.steps || [];
  const totalSteps = steps.length || 9;
  const completed = steps.filter(s => s.isCompleted).length || 0;
  const pct = Math.round((completed / totalSteps) * 100);

  const sysChecks = [
    { label: 'Camera', key: 'camera', icon: '📷' },
    { label: 'Microphone', key: 'microphone', icon: '🎤' },
    { label: 'Internet', key: 'internet', icon: '🌐' },
    { label: 'Browser', key: 'browser', icon: '🖥️' },
  ];

  const renderStep = () => {
    const step = steps[currentStep - 1];
    if (!step) return null;

    /* ── STEP 1: Email Verification ── */
    if (currentStep === 1) return (
      <div className="step-animate">
        <StepTag n={1} />
        <h2 style={S.h2}>Email Verification</h2>
        <p style={S.subtitle}>Verify your registered email address to continue with the interview process.</p>
        <label style={S.fieldLabel}>Registered Email Address</label>
        <input style={S.inputReadonly} readOnly value={data?.application?.email || ''} />
        <div style={S.card}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
            <input type="checkbox" style={{ marginTop: 2, width: 16, height: 16 }} onChange={e => e.target.checked && updateStep(1, {}, 'email_verification')} />
            <span style={{ fontSize: 13, color: T.textSecondary, lineHeight: 1.6 }}>
              I confirm this is my registered email and I agree to the{' '}
              <span
                style={{ color: T.primary, fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}
                onClick={e => { e.preventDefault(); setActiveModal('terms'); }}
              >
                Terms and Conditions
              </span>
              {' '}of this interview platform.
            </span>
          </label>
        </div>
      </div>
    );

    /* ── STEP 2: Disclaimer ── */
    if (currentStep === 2) return (
      <div className="step-animate">
        <StepTag n={2} />
        <h2 style={S.h2}>Disclaimer</h2>
        <p style={S.subtitle}>Please read and accept the following disclaimer before proceeding.</p>
        <div style={S.disclaimerCard}>
          <div style={S.disclaimerTitle}>
            <span style={{ fontSize: 18 }}>📋</span>
            AI Interview Disclaimer
          </div>
          {[
            'This AI Interview will be recorded and analyzed for evaluation purposes.',
            'Your responses (both text and voice) will be stored securely.',
            'Please ensure you are in a quiet environment with good lighting.',
            'By accepting, you consent to AI-powered analysis of your responses.'
          ].map((text, i) => (
            <div key={i} style={{ ...S.disclaimerItem, borderBottom: i === 3 ? 'none' : `1px solid ${T.border}` }}>
              <span style={{ color: T.primary, fontWeight: 700, fontSize: 15, flexShrink: 0 }}>0{i + 1}</span>
              <span>{text}</span>
            </div>
          ))}
        </div>
        <div style={S.btnRow}>
          <button className="btn-primary" style={S.btnPrimary} onClick={() => updateStep(2, {}, 'disclaimer')}>Accept & Continue →</button>
        </div>
      </div>
    );

    /* ── STEP 3: OTP Verification ── */
    if (currentStep === 3) return (
      <div className="step-animate">
        <StepTag n={3} />
        <h2 style={S.h2}>OTP Verification</h2>
        <p style={S.subtitle}>Enter the one-time password sent to your registered email and mobile number.</p>
        <InfoBox>OTP will be sent to <strong>{data?.application?.email}</strong>. Valid for 5 minutes only.</InfoBox>
        {!otpSent ? (
          <button className="btn-primary" style={S.btnPrimary} onClick={sendOTP} disabled={loadingOtp}>
            {loadingOtp ? <><Spinner size={16} color="#fff" /> Sending…</> : '📨 Send OTP'}
          </button>
        ) : (
          <>
            <label style={S.fieldLabel}>Enter OTP</label>
            <div style={S.otpRow}>
              {Array.from({ length: 6 }).map((_, i) => (
                <input key={i} id={`otp-${i}`} style={S.otpInput} maxLength={1} value={otp[i] || ''}
                  onChange={e => {
                    const val = e.target.value.replace(/\D/g, '');
                    if (val.length > 1) return;
                    const arr = otp.split(''); arr[i] = val; setOtp(arr.join(''));
                    if (val && i < 5) document.getElementById(`otp-${i + 1}`)?.focus();
                  }}
                  onKeyDown={e => { if (e.key === 'Backspace' && !otp[i] && i > 0) document.getElementById(`otp-${i - 1}`)?.focus(); }}
                />
              ))}
            </div>
            {timer > 0 && <div style={S.timerText}>⏱ OTP expires in: {Math.floor(timer / 60)}:{String(timer % 60).padStart(2, '0')}</div>}
            <div style={S.divider} />
            <label style={S.fieldLabel}>CAPTCHA Verification</label>
            <div style={S.captchaRow}>
              <div style={S.captchaBox}>{captchaText}</div>
              <button style={S.captchaRefresh} onClick={() => setCaptchaText(generateCaptcha())} title="Refresh CAPTCHA">↻</button>
              <input style={S.captchaInput} placeholder="Type CAPTCHA here" value={captcha} onChange={e => setCaptcha(e.target.value)} />
            </div>
            <div style={S.btnRow}>
              <button className="btn-primary" style={S.btnPrimary} onClick={verifyOTP} disabled={loadingOtp || otp.length !== 6}>
                {loadingOtp ? <><Spinner size={16} color="#fff" /> Verifying…</> : '✓ Sign In'}
              </button>
              <button className="btn-outline" style={S.btnOutline} onClick={sendOTP} disabled={loadingOtp || timer > 0}>
                Resend {timer > 0 && `(${Math.floor(timer / 60)}:${String(timer % 60).padStart(2, '0')})`}
              </button>
            </div>
          </>
        )}
      </div>
    );

    /* ── STEP 4: Review Details ── */
    if (currentStep === 4) return (
      <div className="step-animate">
        <StepTag n={4} />
        <h2 style={S.h2}>Review Interview Details</h2>
        <p style={S.subtitle}>Please review your AI Interview details carefully before proceeding.</p>
        <div style={S.card}>
          {(() => {
            const interviewData = data?.interview || {};
            const applicationData = data?.application || {};
            const requirementData = data?.requirement || applicationData?.requirementId || {};
            const role = applicationData?.appliedRole || interviewData?.role || requirementData?.position || data?.position || '—';
            const company = requirementData?.companyName || applicationData?.companyName || interviewData?.companyName || data?.companyName || '—';
            const candidateName = applicationData?.name || applicationData?.fullName || data?.name || '—';
            const scheduledDate = interviewData?.scheduledDate;
            let dateStr = '—', timeStr = '—';
            if (scheduledDate) {
              try {
                const date = new Date(scheduledDate);
                if (!isNaN(date.getTime())) {
                  dateStr = date.toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
                  timeStr = date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
                }
              } catch (e) {}
            }
            const duration = interviewData?.duration || 60;
            return (
              <>
                <div style={S.detailRow(false)}><span style={S.detailKey}>👤 Candidate</span><span style={S.detailVal}>{candidateName}</span></div>
                <div style={S.detailRow(false)}><span style={S.detailKey}>💼 Role</span><span style={S.detailVal}>{role}</span></div>
                <div style={S.detailRow(false)}><span style={S.detailKey}>🏢 Company</span><span style={S.detailVal}>{company}</span></div>
                <div style={S.detailRow(false)}><span style={S.detailKey}>📅 Date</span><span style={S.detailVal}>{dateStr}</span></div>
                <div style={S.detailRow(false)}><span style={S.detailKey}>🕐 Time</span><span style={S.detailVal}>{timeStr}</span></div>
                <div style={S.detailRow(false)}><span style={S.detailKey}>⏱ Duration</span><span style={S.detailVal}>{duration} minutes</span></div>
                <div style={S.detailRow(true)}><span style={S.detailKey}>📋 Type</span><span style={S.detailVal}>🤖 AI Interview</span></div>
              </>
            );
          })()}
        </div>
        <button className="btn-primary" style={S.btnPrimary} onClick={() => updateStep(4, {}, 'email_verification')}>Confirm Details →</button>
      </div>
    );

    /* ── STEP 5: System Check ── */
    if (currentStep === 5) return (
      <div className="step-animate">
        <StepTag n={5} />
        <h2 style={S.h2}>System Check</h2>
        <p style={S.subtitle}>We'll verify your internet connection, camera, microphone, and browser compatibility for the AI Interview.</p>
        <div style={S.sysGrid}>
          {sysChecks.map(({ label, key, icon }) => {
            const passed = systemCheckStatus[key];
            return (
              <div key={key} style={S.sysItem(passed)}>
                <div style={S.sysIconWrap(passed)}><span style={{ fontSize: 18 }}>{icon}</span></div>
                <div style={{ flex: 1 }}>
                  <div style={S.sysName}>{label}</div>
                  <div style={S.sysStatus(passed)}>{passed ? '✓ Ready' : 'Pending check'}</div>
                </div>
                <div style={S.sysStatusDot(passed)} />
              </div>
            );
          })}
        </div>
        <div style={S.videoBox}>
          <video ref={videoRef} autoPlay playsInline muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>
        <button className="btn-primary" style={S.btnPrimary} onClick={runSystemCheck} disabled={systemCheckStatus.checking}>
          {systemCheckStatus.checking ? <><Spinner size={16} color="#fff" /> Checking…</> : '▶ Run System Check'}
        </button>
      </div>
    );

    /* ── STEP 6: Photo Capture ── */
    if (currentStep === 6) return (
      <div className="step-animate">
        <StepTag n={6} />
        <h2 style={S.h2}>Photo Capture</h2>
        <p style={S.subtitle}>Position your face within the circle for identity verification.</p>
        <div style={S.warningBox}>
          <span style={{ fontSize: 16, flexShrink: 0 }}>⚠️</span>
          <p style={S.warningText}><strong>Note:</strong> Ensure your face is fully visible — no side views, masks, or out-of-frame photos. The circle will guide your positioning.</p>
        </div>
        <div style={S.photoWrap}>
          {!capturedPhoto ? (
            <>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
              <div style={S.faceGuide} />
              {/* ✅ Show placeholder if camera not yet active */}
              {!cameraActive && (
                <div style={{
                  position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.7)',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12,
                }}>
                  <span style={{ fontSize: 32 }}>📷</span>
                  <p style={{ color: '#fff', fontSize: 13 }}>Starting camera...</p>
                </div>
              )}
            </>
          ) : (
            <img src={capturedPhoto} alt="Captured" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          )}
        </div>
        {!capturedPhoto ? (
          <div style={S.btnRow}>
            <button
              className="btn-primary"
              style={{ ...S.btnPrimary, opacity: cameraActive ? 1 : 0.6 }}
              onClick={capturePhoto}
              disabled={!cameraActive}
            >
              📸 {cameraActive ? 'Capture Photo' : 'Starting Camera...'}
            </button>
            {!cameraActive && (
              <button className="btn-outline" style={S.btnOutline} onClick={startCamera}>
                🔄 Enable Camera
              </button>
            )}
          </div>
        ) : (
          <div style={S.btnRow}>
            <button className="btn-outline" style={S.btnOutline} onClick={retakePhoto}>↩ Retake Photo</button>
            <button className="btn-success" style={S.btnSuccess} onClick={() => updateStep(6, { photoUrl: capturedPhoto }, 'photo_verification')}>✓ Confirm & Continue</button>
          </div>
        )}
      </div>
    );

    /* ── STEP 7: Mic Test ── */
    if (currentStep === 7) return (
      <div className="step-animate">
        <StepTag n={7} />
        <h2 style={S.h2}>Microphone Test</h2>
        <p style={S.subtitle}>Verify your microphone works correctly for the AI Interview voice responses.</p>
        <div style={S.phraseCard}>
          <div style={S.phraseLabel}>Read this phrase aloud</div>
          <p style={S.phraseText}>"Artificial Intelligence is transforming the future of work."</p>
        </div>
        <InfoBox>Ensure you are in a quiet environment. Pronounce words clearly for the AI to recognize your voice. You can try again if needed.</InfoBox>
        <button className="btn-primary" style={S.btnPrimary} onClick={() => updateStep(7, { transcript: 'Test completed' }, 'mic_test')}>🎤 Initiate Mic Test</button>
      </div>
    );

    /* ── STEP 8: Camera & Consent ── */
    if (currentStep === 8) return (
      <div className="step-animate">
        <StepTag n={8} />
        <h2 style={S.h2}>Camera & Microphone Setup</h2>
        <p style={S.subtitle}>Please set up your camera and microphone before starting the AI Interview.</p>

        {/* Camera Preview */}
        <div style={styles.cameraSetupCard}>
          <div style={styles.cameraHeader}>
            <span style={styles.cameraTitle}>📹 Camera Preview</span>
            <span style={styles.cameraStatus}>
              <span style={{ ...styles.statusDot, backgroundColor: cameraActive ? '#10B981' : '#EF4444' }} />
              {cameraActive ? 'Camera Active' : 'Camera Inactive'}
            </span>
          </div>
          <div style={styles.cameraPreview}>
            <video ref={videoRef} autoPlay playsInline muted style={styles.previewVideo} />
            {!cameraActive && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, background: 'rgba(0,0,0,0.8)' }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <p style={{ color: '#D1D5DB', fontSize: 13, textAlign: 'center', padding: '0 20px' }}>Camera not active. Click below to allow camera access.</p>
                <button style={styles.startCameraBtn} onClick={startCamera}>Start Camera</button>
              </div>
            )}
          </div>
        </div>

        {/* Mic Test */}
        <div style={styles.micTestCard}>
          <div style={styles.micHeader}>
            <span style={styles.micTitle}>🎤 Microphone Test</span>
            <span style={styles.micStatus}>
              <span style={{ ...styles.statusDot, backgroundColor: systemCheckStatus.microphone ? '#10B981' : '#EF4444' }} />
              {systemCheckStatus.microphone ? 'Microphone Working' : 'Test Required'}
            </span>
          </div>
          <div style={styles.micContent}>
            <p style={styles.micInstruction}>Read the following sentence aloud to test your microphone:</p>
            <div style={styles.testPhrase}>
              "Artificial Intelligence is transforming the future of work and recruitment."
            </div>
            <button style={{ ...styles.testMicBtn, opacity: systemCheckStatus.microphone ? 0.7 : 1 }} onClick={runSystemCheck}>
              {systemCheckStatus.microphone ? '✅ Microphone Tested — Test Again' : '🎙️ Test Microphone'}
            </button>
            {systemCheckStatus.microphone && (
              <div style={styles.micSuccess}>✅ Microphone is working properly!</div>
            )}
          </div>
        </div>

        {/* Consent Agreements — ✅ uses React state instead of getElementById */}
        <div style={styles.consentCard}>
          <div style={styles.consentHeader}>
            <span style={styles.consentTitle}>📋 Consent & Agreement</span>
          </div>
          <div style={styles.consentContent}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                style={styles.checkbox}
                checked={privacyConsent}
                onChange={e => setPrivacyConsent(e.target.checked)}
              />
              <span style={styles.checkboxText}>
                I agree to the processing of personal data in accordance with the{' '}
                <span style={styles.linkText} onClick={() => setActiveModal('privacy')}>Privacy Policy</span>
              </span>
            </label>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                style={styles.checkbox}
                checked={aiConsent}
                onChange={e => setAiConsent(e.target.checked)}
              />
              <span style={styles.checkboxText}>
                I agree to the processing and analysis of my answers using{' '}
                <span style={styles.linkText} onClick={() => setActiveModal('ai')}>artificial intelligence technologies</span>
              </span>
            </label>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                style={styles.checkbox}
                checked={recordingConsent}
                onChange={e => setRecordingConsent(e.target.checked)}
              />
              <span style={styles.checkboxText}>
                I consent to{' '}
                <span style={styles.linkText} onClick={() => setActiveModal('recording')}>recording my video and audio</span>{' '}
                during this interview for evaluation purposes
              </span>
            </label>
          </div>
        </div>

        {/* Instructions */}
        <div style={styles.instructionsCard}>
          <div style={styles.instructionsHeader}>
            <span style={styles.instructionsTitle}>📖 Answering Instructions for Candidates</span>
          </div>
          <div style={styles.instructionsContent}>
            <ul style={{ ...styles.instructionsList, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', textAlign: 'left' }}>
              {[
                'Ensure you are in a quiet, well-lit environment',
                'Speak clearly and at a moderate pace',
                'Read each question carefully before answering',
                'You can answer by typing or recording your voice',
                'Each question has a time limit — watch the timer',
                'Do not switch tabs or leave the interview window',
                'Ensure your face is clearly visible throughout',
                'Technical issues? Contact support immediately',
              ].map((item, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: '#92400E', lineHeight: 1.5 }}>
                  <span style={{ color: '#E65100', fontWeight: 700, flexShrink: 0 }}>{i + 1}.</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Readiness summary */}
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, padding: '16px 20px', marginBottom: 24, boxShadow: T.shadow }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 12 }}>Pre-interview Checklist</div>
          {[
            { label: 'Camera active', ok: cameraActive },
            { label: 'Microphone tested', ok: systemCheckStatus.microphone },
            { label: 'Privacy Policy accepted', ok: privacyConsent },
            { label: 'AI Processing consent given', ok: aiConsent },
            { label: 'Recording consent given', ok: recordingConsent },
          ].map(({ label, ok }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: `1px solid ${T.border}` }}>
              <span style={{
                width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                background: ok ? T.successBg : T.errorBg,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11,
              }}>{ok ? '✓' : '✗'}</span>
              <span style={{ fontSize: 13, color: ok ? T.success : T.textSecondary }}>{label}</span>
            </div>
          ))}
        </div>

        {/* Continue Button */}
        <div style={styles.continueSection}>
          <button
            className="btn-primary"
            style={{
              ...styles.continueBtn,
              opacity: (privacyConsent && aiConsent && recordingConsent && systemCheckStatus.microphone && cameraActive) ? 1 : 0.5,
            }}
            onClick={() => {
              if (!privacyConsent) { showNotification('error', 'Please accept the Privacy Policy to continue'); return; }
              if (!aiConsent) { showNotification('error', 'Please consent to AI processing to continue'); return; }
              if (!recordingConsent) { showNotification('error', 'Please consent to recording to continue'); return; }
              if (!systemCheckStatus.microphone) { showNotification('error', 'Please test your microphone before continuing'); return; }
              if (!cameraActive) { showNotification('error', 'Please enable your camera before continuing'); return; }
              updateStep(8, { privacyConsent: true, aiConsent: true, recordingConsent: true, cameraActive: true, micActive: true }, 'screen_share');
            }}
          >
            Continue to Interview →
          </button>
          <p style={styles.waitingText}>
            All items above must be completed before you can continue.
          </p>
        </div>
      </div>
    );

    /* ── STEP 9: Start Interview ── */
    if (currentStep === 9) return (
      <div className="step-animate">
        <StepTag n={9} />
        <h2 style={S.h2}>Ready to Begin AI Interview</h2>
        <div style={S.ctaCard}>
          <div style={S.ctaIconWrap}>
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <circle cx="16" cy="16" r="14" stroke={T.primary} strokeWidth="2" />
              <path d="M11 16l4 4 7-8" stroke={T.primary} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h3 style={S.ctaTitle}>All Prerequisites Completed!</h3>
          <p style={S.ctaText}>You've successfully completed all required steps. Click below to start your AI Interview. Good luck!</p>
          <button
            className="btn-primary"
            style={{ ...S.btnPrimary, ...S.btnLarge }}
            onClick={() => { document.documentElement.requestFullscreen?.(); updateStep(9, {}, 'fullscreen'); }}
          >
            🚀 Start AI Interview
          </button>
        </div>
      </div>
    );

    return null;
  };

  /* ── AI Mode ── */
  if (showAIMode) return (
    <>
      <style>{globalStyles}</style>
      <AIInterview
        interviewId={interviewId}
        applicationId={currentApplicationId}
        candidateInfo={candidateInfo}
        onComplete={() => { setShowAIMode(false); navigate('/dashboard'); }}
      />
    </>
  );

  if (loading) return (
    <div style={S.loadingWrap}>
      <style>{globalStyles}</style>
      <Spinner size={44} />
      <p style={{ color: T.textSecondary, fontSize: 15 }}>Loading your AI Interview preparation...</p>
    </div>
  );

  if (!data) return (
    <div style={S.loadingWrap}>
      <style>{globalStyles}</style>
      <p style={{ color: T.textSecondary }}>No data found.</p>
    </div>
  );

  if (showTimeRestrictionModal && timeRestrictionData) return (
    <>
      <style>{globalStyles}</style>
      <TimeRestrictionModal data={timeRestrictionData} onClose={() => { setShowTimeRestrictionModal(false); navigate('/'); }} />
    </>
  );

  if (showAssessmentMode && assessmentId) return (
    <>
      <style>{globalStyles}</style>
      <AssessmentMode assessmentId={assessmentId} applicationId={currentApplicationId} candidateInfo={candidateInfo} onComplete={() => { setShowAssessmentMode(false); navigate('/dashboard'); }} />
    </>
  );

  if (showKioskMode && meetingUrl) return (
    <>
      <style>{globalStyles}</style>
      <KioskInterviewMode meetingUrl={meetingUrl} onComplete={() => { setShowKioskMode(false); navigate('/dashboard'); }} interviewId={currentInterviewId} applicationId={currentApplicationId} />
    </>
  );

  const firstName = data?.application?.name?.split(' ')[0] || 'Candidate';

  return (
    <div style={S.wrap}>
      <style>{globalStyles}</style>

      {/* ✅ Policy Modals */}
      {activeModal && (
        <PolicyModal
          type={activeModal}
          onClose={() => setActiveModal(null)}
          onAccept={activeModal === 'privacy' ? () => setPrivacyConsent(true)
            : activeModal === 'ai' ? () => setAiConsent(true)
            : activeModal === 'recording' ? () => setRecordingConsent(true)
            : null}
        />
      )}

      {/* Navbar */}
      <nav style={S.navbar}>
        <div style={S.logoWrap}>
          <div style={S.logoIcon}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <circle cx="9" cy="9" r="7" stroke="white" strokeWidth="1.8" />
              <path d="M6 9l2 2 4-4" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <span style={S.logoText}>Humanex</span>
        </div>
        <div style={S.navRight}>
          <span style={{ background: '#E8F5E9', padding: '5px 14px', borderRadius: 20, fontSize: 12, color: '#2E7D32', fontWeight: 600 }}>
            🤖 AI Interview Mode
          </span>
          <div style={S.userChip}>
            <div style={S.userAvatar}>{firstName[0]}</div>
            <span style={S.userName}>Hello, {firstName}</span>
          </div>
        </div>
      </nav>

      {/* Body */}
      <div style={S.body}>
        {/* Sidebar */}
        <aside style={S.sidebar}>
          <div style={S.sidebarHead}>
            <span style={S.sidebarTitle}>Prerequisites</span>
            <div style={S.progressWrap}>
              <div style={S.progressTrack}><div style={S.progressFill(pct)} /></div>
              <div style={S.progressLabel}>
                <span>{completed} of {totalSteps} completed</span>
                <span style={{ fontWeight: 600, color: T.primary }}>{pct}%</span>
              </div>
            </div>
          </div>
          <div style={S.sidebarSteps}>
            {STEP_LABELS.map((label, idx) => {
              const stepData = steps[idx];
              const isDone = stepData?.isCompleted;
              const isActive = currentStep === idx + 1;
              return (
                <div key={idx} className="sidebar-item" style={S.stepItem(isActive, isDone)}
                  onClick={() => (isDone || isActive) && setCurrentStep(idx + 1)}>
                  <div style={S.stepBubble(isActive, isDone)}>
                    {isDone ? (
                      <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                        <polyline points="1.5,5.5 4.5,8.5 9.5,2" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ) : idx + 1}
                  </div>
                  <span style={S.stepLabel(isActive, isDone)}>{label}</span>
                </div>
              );
            })}
          </div>
        </aside>

        {/* Main */}
        <main style={S.main}>{renderStep()}</main>
      </div>
    </div>
  );
};

export default InterviewPrerequisites;